import React, { useEffect, useState } from "react";

// lib
import { cn } from "../../../lib/utils";

import { Tooltip } from "../../molecules/Tooltip";
// components
import { InputBase, InputBaseProps } from "../InputBase";

type BaseProps = Omit<
	InputBaseProps,
	"value" | "onChange" | "spellCheck" | "minLength" | "maxLength" | "type" | "pattern" | "inputMode"
> & {
	/**
	 * The step value of the input
	 * @default 1
	 */
	step?: number;
	/**
	 * Whether to parse the value on blur
	 * @default false
	 */
	parseOnBlur?: boolean;
	/**
	 * The precision of the input (number of decimal places)
	 * @default 2
	 */
	precision?: number;
	/**
	 * Whether to use thousands separator
	 * @default false
	 */
	useThousandsSeparator?: boolean;
	/**
	 * Whether to keep trailing zeros up to the full precision, e.g. "1000.20"
	 * instead of "1000.2". Use for money, where cents are always two digits.
	 * @default false
	 */
	padDecimals?: boolean;
};

const CarrotUp = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
		<path
			d="M0.235526 5.22186L3.42183 2.01299C3.60007 1.85506 3.80564 1.77734 4.01121 1.77734C4.21677 1.77734 4.42184 1.85569 4.57852 2.01237L7.76483 5.22123C7.99436 5.45077 8.06315 5.79557 7.93888 6.09565C7.81462 6.39573 7.54347 6.59064 7.19751 6.59064H0.802592C0.478196 6.59064 0.185387 6.39558 0.0610436 6.09552C-0.0633 5.79547 0.00597392 5.44999 0.235526 5.22186Z"
			fill="currentColor"
		/>
	</svg>
);

const CarrotDown = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
		<path
			d="M7.78037 3.14519L4.59625 6.35186C4.41838 6.50969 4.21295 6.58735 4.00752 6.58735C3.80209 6.58735 3.59717 6.50906 3.44059 6.35249L0.256467 3.14582C0.00480378 2.91722 -0.0638491 2.5715 0.0602592 2.27338C0.184367 1.97526 0.476926 1.77734 0.80085 1.77734H7.19165C7.51582 1.77734 7.80843 1.97227 7.93269 2.27212C8.05695 2.57197 8.01085 2.91722 7.78037 3.14519Z"
			fill="currentColor"
		/>
	</svg>
);

type InputNumericProps = BaseProps &
	(
		| {
				/**
				 * Whether to allow empty/null values
				 */
				allowEmpty: true;
				/**
				 * The value of the input
				 */
				value: number | null;
				/**
				 * The onChange event handler
				 */
				onChange: (value: number | null) => void;
		  }
		| {
				/**
				 * Whether to allow empty/null values
				 */
				allowEmpty?: false;
				/**
				 * The value of the input
				 */
				value: number;
				/**
				 * The onChange event handler
				 */
				onChange: (value: number) => void;
		  }
	);

const isNumberWithCommasAndMinusRegex = /^[\d.,-]*$/;

// Format the input value for display
const format = (num: number, precision: number, useThousandsSeparator: boolean, padDecimals: boolean): string => {
	const fixed = num.toFixed(precision);
	const parts = fixed.split(".");
	if (useThousandsSeparator) {
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	}
	// Only remove trailing zeros after decimal point
	if (parts[1] && !padDecimals) {
		parts[1] = parts[1].replace(/0+$/, "");
		if (!parts[1]) {
			parts.pop();
		}
	}
	return parts.join(".");
};

/**
 * InputNumeric is an Isotope of InputBase with additional props for numeric input.
 * It is a controlled component that uses the value and onChange props to update the input value.
 * It also has a right action element for increment and decrement.
 * It is used outside of a form context. If you need a form-connected numeric input, use FormInputNumeric
 */
const InputNumeric = ({
	value,
	onChange,
	onBlur,
	onFocus,
	min,
	max,
	step = 1,
	parseOnBlur = false,
	precision = 2,
	disabled,
	errored,
	dangerouslySetClassName,
	allowEmpty = false,
	id,
	useThousandsSeparator = false,
	padDecimals = false,
	"data-testid": dataTestId,
	leftIcon,
	placeholder,
	clearable,
	size,
	flashKey,
}: InputNumericProps) => {
	// display string value
	const [internal, setInternal] = useState(() =>
		value === null ? "" : format(value, precision, useThousandsSeparator, padDecimals)
	);

	useEffect(() => {
		// A value that already parses to what the input says is the parent echoing
		// our own onChange back mid-edit. Reformatting then rewrites the field under
		// the user (deleting the "2" of "1000.20" snaps to "1000.00"), so only sync
		// when the value genuinely differs; blur formats the final string.
		if (parse(internal) === value) return;
		setInternal(value === null ? "" : format(value, precision, useThousandsSeparator, padDecimals));
	}, [value, precision, useThousandsSeparator, padDecimals]);

	// Parse the input value
	const parse = (raw: string): number | null => {
		const cleaned = raw.replace(/,/g, "");
		const num = parseFloat(cleaned);
		if (isNaN(num)) return allowEmpty ? null : 0;
		if (min !== undefined && num < min) return min;
		if (max !== undefined && num > max) return max;
		return +num.toFixed(precision);
	};

	// Handle the change event
	const handleChange = (raw: string) => {
		if (isNumberWithCommasAndMinusRegex.test(raw) || raw === "" || raw === "-" || raw === ".") {
			setInternal(raw);
			if (!parseOnBlur) {
				const parsed = parse(raw);
				if (parsed !== null) {
					onChange(parsed);
				}
			}
		}
	};

	// Handle the blur event
	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const parsed = parse(internal);
		setInternal(parsed === null ? "" : format(parsed, precision, useThousandsSeparator, padDecimals));
		if (parsed === null) {
			if (allowEmpty) {
				(onChange as (value: number | null) => void)(null);
			} else {
				(onChange as (value: number) => void)(0);
			}
		} else {
			onChange(parsed);
		}
		onBlur?.(e);
	};

	// Increment the value
	const increment = () => {
		const currentValue = parse(internal) ?? 0;
		// Convert to fixed-point integer to avoid floating-point errors
		const factor = Math.pow(10, precision);
		const newValue = Math.round(currentValue * factor + step * factor) / factor;
		if (max === undefined || newValue <= max) {
			setInternal(format(newValue, precision, useThousandsSeparator, padDecimals));
			onChange(newValue);
		}
	};

	// Decrement the value
	const decrement = () => {
		const currentValue = parse(internal) ?? 0;
		// Convert to fixed-point integer to avoid floating-point errors
		const factor = Math.pow(10, precision);
		const newValue = Math.round(currentValue * factor - step * factor) / factor;
		if (min === undefined || newValue >= min) {
			setInternal(format(newValue, precision, useThousandsSeparator, padDecimals));
			onChange(newValue);
		}
	};

	// Handle the key down event
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowUp") {
			e?.preventDefault();
			increment();
		} else if (e.key === "ArrowDown") {
			e?.preventDefault();
			decrement();
		}
	};

	const carrotButtonClassNames = `text-neutral-5 hover:text-neutral-7 active:text-neutral-7 focus:text-neutral-7 m-0 h-1 border-none bg-transparent p-0 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`;
	// Right action element
	const rightActionElement = (
		<div className="flex h-full flex-col items-center justify-center">
			<Tooltip content={`Increase value by ${step}`} size="medium" asChild>
				<button
					type="button"
					className={cn(carrotButtonClassNames, "flex h-2 w-2 items-center justify-center p-0")}
					onClick={(e) => {
						e.preventDefault();
						increment();
					}}
					disabled={disabled || (max !== undefined && parse(internal) === max)}
					aria-label={`Increase value by ${step}`}
					aria-disabled={disabled || (max !== undefined && parse(internal) === max)}
					data-testid={`${dataTestId}-increment`}
					aria-controls={id}
					tabIndex={-1}
				>
					<CarrotUp />
				</button>
			</Tooltip>
			<Tooltip content={`Decrease value by ${step}`} size="medium" asChild>
				<button
					type="button"
					className={cn(carrotButtonClassNames, "flex h-2 w-2 items-center justify-center p-0")}
					onClick={(e) => {
						e.preventDefault();
						decrement();
					}}
					disabled={disabled || (min !== undefined && parse(internal) === min)}
					aria-label={`Decrease value by ${step}`}
					aria-disabled={disabled || (min !== undefined && parse(internal) === min)}
					data-testid={`${dataTestId}-decrement`}
					aria-controls={id}
					tabIndex={-1}
				>
					<CarrotDown />
				</button>
			</Tooltip>
		</div>
	);

	return (
		<InputBase
			value={internal}
			onChange={handleChange}
			onBlur={handleBlur}
			onFocus={onFocus}
			onKeyDown={handleKeyDown}
			type="text"
			inputMode="decimal"
			pattern="-?[0-9]*\.?[0-9]*"
			disabled={disabled}
			errored={errored}
			dangerouslySetClassName={dangerouslySetClassName}
			dangerouslySetInputClassName={cn(
				"aria-[role=spinbutton]",
				`aria-valuenow="${parse(internal) ?? ""}"`,
				min !== undefined ? `aria-valuemin="${min}"` : "",
				max !== undefined ? `aria-valuemax="${max}"` : "",
				`aria-valuetext="${internal}"`,
				errored ? `aria-describedby="${id}-error"` : ""
			)}
			leftIcon={leftIcon}
			placeholder={placeholder}
			clearable={clearable}
			id={id}
			data-testid={dataTestId}
			rightActionElement={rightActionElement}
			aria-label={`${id} number input`}
			size={size}
			flashKey={flashKey}
		/>
	);
};

export { InputNumeric, type InputNumericProps };
